<template>
  <div class="interference-pro-fixed">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$Δr = nλ ⇒ Max | Δr = (n+1/2)λ ⇒ Min$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>波源间距 d</label>
            <span class="val">{{ d.toFixed(1) }} <small>λ</small></span>
          </div>
          <div class="hud-item orange">
            <label>相位差 Δφ</label>
            <span class="val">{{ phaseDiff.toFixed(2) }} <small>rad</small></span>
          </div>
          <div class="hud-item green">
             <label>状态</label>
             <span class="val">相干干涉场已建立</span>
          </div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 450 240" preserveAspectRatio="xMidYMid meet">
          <defs>
             <radialGradient id="ring-blue"><stop offset="0%" stop-color="#3b82f6" stop-opacity="0.8"/><stop offset="100%" stop-color="#3b82f6" stop-opacity="0"/></radialGradient>
          </defs>

          <!-- 干涉条纹位图模拟 (Lattice) -->
          <g v-for="y in 12" :key="'y'+y">
            <circle v-for="x in 20" :key="'x'+x" 
                    :cx="x*22+5" :cy="y*20-10" 
                    :r="getInterferenceInten(x*22+5, y*20-10)" 
                    :fill="getIntenColor(x*22+5, y*20-10)" opacity="0.6" />
          </g>

          <!-- 两个相干波源 (S1, S2) -->
          <circle :cx="225" :cy="120 - d*5" r="5" fill="#f43f5e" filter="url(#glow-s)" />
          <circle :cx="225" :cy="120 + d*5" r="5" fill="#f43f5e" filter="url(#glow-s)" />
          <text x="210" :y="120 - d*5" fill="#f43f5e" font-size="9">S₁</text>
          <text x="210" :y="120 + d*5" fill="#f43f5e" font-size="9">S₂</text>
          
          <defs>
             <filter id="glow-s"><feGaussianBlur stdDeviation="3" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
          </defs>
        </svg>

        <div class="physics-caption">
           规律：振动加强区和减弱区相互交替，形成稳定的干扰图样。
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex-row items-center gap-6">
              <div class="flex-1">
                 <label class="s-label">调节波源间距 (d/λ)：{{ d }}</label>
                 <el-slider v-model="d" :min="2" :max="20" size="small"></el-slider>
              </div>
              <div class="flex-1">
                 <label class="s-label">调节波源频率 (f)：{{ freq }} Hz</label>
                 <el-slider v-model="freq" :min="1" :max="10" size="small"></el-slider>
              </div>
           </div>
        </div>
        
        <!-- 原子化专一高对比度解释 -->
        <div class="discovery-card-discovery-tir" v-latex>
           <div class="icon-wrap"><i class="el-icon-odometer"></i></div>
           <div class="text-content">
             <p><strong>实战物理/隔声降噪：</strong>干涉不仅仅是叠加。<strong>解惑：</strong>当两列波波峰撞击波谷，能量会相互杀灭（节点带）。<strong>观察：</strong>调节间距 $d$，你会看到屏幕上斜出的“空白条带”数量在变化。这就是为什么有的地方声音特别大，有的地方却听不见。现代降噪耳机正是制造频率一致但相位相反的“干涉波”杀灭了噪音。
             </p>
           </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'CoherentWaveInterferenceViz',
  data() {
    return {
      d: 6,
      freq: 5,
      phaseDiff: 0,
      time: 0
    }
  },
  mounted() { this.animate(); },
  methods: {
    getInterferenceInten(x, y) {
       const r1 = Math.hypot(x - 225, y - (120 - this.d*5));
       const r2 = Math.hypot(x - 225, y - (120 + this.d*5));
       const wave1 = Math.sin(r1 * 0.15 - this.time * 0.2);
       const wave2 = Math.sin(r2 * 0.15 - this.time * 0.2 + this.phaseDiff);
       const combined = Math.abs(wave1 + wave2);
       return 2 + combined * 3;
    },
    getIntenColor(x, y) {
       const r1 = Math.hypot(x - 225, y - (120 - this.d*5));
       const r2 = Math.hypot(x - 225, y - (120 + this.d*5));
       const pathDiff = Math.abs(r1 - r2);
       // Path difference as multiple of half-lambda (lambda approx 42 in visual units)
       const lambda = 42;
       const mod = (pathDiff % lambda) / lambda;
       if (mod < 0.1 || mod > 0.9) return "#06b6d4"; // Antinode (Construction)
       if (Math.abs(mod - 0.5) < 0.1) return "#334155"; // Node (Destruction)
       return "#1e293b";
    },
    animate() {
       this.time += 1;
       this.animFrame = requestAnimationFrame(this.animate);
    }
  },
  beforeDestroy() {
    if (this.animFrame) cancelAnimationFrame(this.animFrame);
    if (this.timer) clearInterval(this.timer);
  },
}
</script>

<style scoped lang="scss">
@import "./PhysicsCommon.scss";
.interference-pro-fixed { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
</style>
